import React, { useCallback, useState } from 'react';
import * as yup from 'yup';
import { Grid, makeStyles } from '@material-ui/core';
import BaseButton from '../../Components/Buttons/BaseButton';
import TextInput from '../../Components/Inputs/TextInput';
import ShowPassword from '../../Components/Adornments/ShowPassword';
import SimpleDivider from '../../Components/Dividers/SimpleDivider';
import { useForm } from 'react-hook-form';
import { UpdateUserPasswordAPI } from '../../API/UserAPI';
import DeleteAccountDialog from './DeleteAccountDialog';
import { updateUser } from '../../Utils/User/Actions';
import UnlinkAppleAccountDialog from './UnlinkAppleAccountDialog';
import { logoutUser } from '../../Utils/User/Actions';
import { isMobileApp } from '../../Utils/Mobile/actions';
import _get from 'lodash/get';
import PasswordInputStrengthMeter from '../../Components/PasswordStrengthIndicator/PasswordInputStrengthMeter';
import SweetAlert from '../../Components/Alerts/SweetAlert';
import { hasUserSsoCredentials } from '../../Enums/users';
import { yupResolver } from '@hookform/resolvers/yup';

const passwordSchema = yup.object({
  currentPassword: yup
    .string()
    .required('Ingresa una contraseña')
    .min(8, 'Debes usar al menos 8 caracteres')
    .notOneOf(
      [yup.ref('newPassword')],
      'Las contraseña debe ser diferente a la nueva'
    ),
  newPassword: yup
    .string()
    .required('')
    .min(8, '')
    .notOneOf(
      [yup.ref('currentPassword')],
      'La nueva contraseña debe ser diferente a la actual'
    )
    .test('is-strong-password', '', (_, context) => {
      return context.options.context.isPasswordStrong === true;
    }),
  confirmPassword: yup
    .string()
    .required('Ingresa de nuevo la contraseña')
    .oneOf([yup.ref('newPassword')], 'Las contraseñas deben ser iguales')
});

const noPasswordSchema = yup.object({
  newPassword: yup
    .string()
    .required('Ingresa una contraseña')
    .min(8, 'Debes usar al menos 8 caracteres'),
  confirmPassword: yup
    .string()
    .required('Ingresa de nuevo la contraseña')
    .oneOf([yup.ref('newPassword')], 'Las contraseñas deben ser iguales')
});

// eslint-disable-next-line complexity
const MyProfileUserPassword = props => {
  const {
    currentUser,
    setSelectedItem,
    setLoading,
    setAlert,
    setCurrentUser,
    setPasswordDialog,
    canUpdatePassword = true
  } = props;

  const classes = useStyles();

  const [currentPasswordShown, setCurrentPasswordShown] = useState('');
  const [confirmPasswordShown, setConfirmPasswordShown] = useState('');
  const [openDeleteAccountDialog, setOpenDeleteAccountDialog] = useState(false);
  const [
    openUnlinkAppleAccountDialog,
    setOpenUnlinkAppleAccountDialog
  ] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [isPasswordStrong, setIsPasswordStrong] = useState(false);

  const {
    handleSubmit: handleSubmitPassword,
    register: registerPassword,
    errors: errorsPassword,
    setError: setErrorPassword,
    formState: formStatePassword,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(passwordSchema),
    context: { isPasswordStrong },
    mode: 'onChange'
  });

  const {
    handleSubmit: handleSubmitNoPassword,
    register: registerNoPassword,
    errors: errorsNoPassword,
    formState: formStateNoPassword
  } = useForm({
    resolver: yupResolver(noPasswordSchema),
    mode: 'onChange'
  });

  const onToggleCurrentPasswordShown = useCallback(() => {
    setCurrentPasswordShown(v => !v);
  }, []);

  const onToggleConfirmPasswordShown = useCallback(() => {
    setConfirmPasswordShown(v => !v);
  }, []);

  const newPwdValue = watch('newPassword');
  const confirmPwdValue = watch('confirmPassword');

  const handlePasswordErrors = useCallback(
    (validationErrors, strength, isStrong) => {
      setPasswordErrors(validationErrors || []);

      if (isStrong === isPasswordStrong) {
        setIsPasswordStrong(isStrong);
        return;
      }

      setIsPasswordStrong(isStrong);
      setTimeout(() => {
        if (newPwdValue) {
          setValue('newPassword', newPwdValue, { shouldValidate: true });
        }

        if (confirmPwdValue) {
          setValue('confirmPassword', confirmPwdValue, {
            shouldValidate: true
          });
        }
      }, 0);
    },
    [isPasswordStrong, newPwdValue, confirmPwdValue, setValue]
  );

  const onSubmitPassword = useCallback(
    async values => {
      try {
        setLoading(true);
        const response = await UpdateUserPasswordAPI(
          currentUser.token,
          currentUser.id,
          {
            currentPassword: values.currentPassword,
            password: values.newPassword
          }
        );

        if (!response.success) {
          const errorMessage = _get(
            response,
            'error.response.data.errors.password',
            'La contraseña actual no es válida'
          );
          setErrorPassword('currentPassword', 'notOneOf', errorMessage);
          return;
        }

        const { token, appToken } = response.data.data;

        setAlert({
          type: 'success',
          message: 'Contraseña actualizada satisfactoriamente'
        });

        updateUser(setCurrentUser, token, appToken);
        setSelectedItem(1);
      } catch (error) {
        setAlert({
          type: 'error',
          message: 'Error al actualizar la contraseña'
        });
      } finally {
        setLoading(false);
      }
    },
    [
      currentUser,
      setErrorPassword,
      setAlert,
      setLoading,
      setCurrentUser,
      setSelectedItem
    ]
  );

  const onDeleteAccountPress = () => {
    setOpenDeleteAccountDialog(true);
  };

  const onSuccessDeleteAccount = useCallback(() => {
    if (!isMobileApp()) {
      setAlert({
        type: 'success',
        message: 'Tu cuenta ha sido eliminada exitosamente'
      });
    }
    logoutUser(setCurrentUser, true);
  }, [setCurrentUser, setAlert]);

  const showAuthenticationTypeBanner = useCallback(() => {
    if (
      !currentUser ||
      (currentUser.authenticationType &&
        Object.keys(currentUser.authenticationType).length > 0)
    ) {
      return <></>;
    }
    return (
      <SweetAlert
        id="MyProfile_authentication_alert"
        type="info"
        classes={{
          root: classes.sweetAlert,
          message: classes.sweetAlertText
        }}
        message={
          'Para mayor seguridad, vincula una red social a tu cuenta para iniciar sesión o usa un administrador de contraseña'
        }
      />
    );
  }, [classes, currentUser]);

  const shouldShowPasswordForm = () => {
    return canUpdatePassword;
  };

  if (!currentUser) {
    return <></>;
  }

  const handleSubmit = currentUser.hasPassword
    ? handleSubmitPassword
    : handleSubmitNoPassword;
  const register = currentUser.hasPassword
    ? registerPassword
    : registerNoPassword;
  const errors = currentUser.hasPassword ? errorsPassword : errorsNoPassword;
  const formState = currentUser.hasPassword
    ? formStatePassword
    : formStateNoPassword;

  const isDeleteAccountDisabled =
    currentUser.passwordChangeRequired && !hasUserSsoCredentials(currentUser);

  return (
    <>
      {openDeleteAccountDialog && (
        <DeleteAccountDialog
          open={openDeleteAccountDialog}
          setDialog={setOpenDeleteAccountDialog}
          setPasswordDialog={setPasswordDialog}
          currentUser={currentUser}
          onSuccessDeleteAccount={onSuccessDeleteAccount}
          setOpenUnlinkAppleAccountDialog={setOpenUnlinkAppleAccountDialog}
        />
      )}
      {openUnlinkAppleAccountDialog && (
        <UnlinkAppleAccountDialog
          open={openUnlinkAppleAccountDialog}
          setDialog={setOpenUnlinkAppleAccountDialog}
          currentUser={currentUser}
          setIsAppleLoading={setIsAppleLoading}
          onSuccessDeleteAccount={onSuccessDeleteAccount}
          isLoading={isAppleLoading}
        />
      )}

      {shouldShowPasswordForm() && showAuthenticationTypeBanner()}

      {shouldShowPasswordForm() && (
        <form
          key={2}
          id="editPasswordForm"
          onSubmit={handleSubmit(onSubmitPassword)}
        >
          {currentUser.hasPassword && (
            <Grid
              item
              sm={12}
              className={`${classes.inputFieldContainer} ${errors.name &&
                classes.optionWithErrors}`}
            >
              <TextInput
                inputRef={register}
                id="MyProfile_input_currentPassword"
                required
                fullWidth
                margin="none"
                label="Contraseña actual"
                error={Boolean(errors.currentPassword)}
                helperText={
                  errors.currentPassword && errors.currentPassword.message
                }
                name="currentPassword"
                type={currentPasswordShown ? 'text' : 'password'}
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <ShowPassword
                      togglePasswordShow={onToggleCurrentPasswordShown}
                      passwordShown={currentPasswordShown}
                    />
                  )
                }}
              />
            </Grid>
          )}{' '}
          <Grid
            item
            sm={12}
            className={`${classes.inputFieldContainer} ${errors.name &&
              classes.optionWithErrors}`}
          >
            <PasswordInputStrengthMeter
              password={watch('newPassword') || ''}
              currentPassword={watch('currentPassword')}
              onErrorsChange={handlePasswordErrors}
              username={`${currentUser.firstName} ${currentUser.lastName}`}
              withInput={true}
              inputRef={register}
              label="Nueva contraseña"
              error={
                isPasswordStrong
                  ? false
                  : Boolean(errors.newPassword || passwordErrors.length > 0)
              }
              helperText=""
              name="newPassword"
              onChange={e => setValue('newPassword', e.target.value)}
            />
          </Grid>
          <Grid
            item
            sm={12}
            className={`${classes.inputFieldContainer} ${errors.name &&
              classes.optionWithErrors}`}
          >
            <TextInput
              inputRef={register}
              id="MyProfile_input_confirmPassword"
              required
              fullWidth
              margin="none"
              label="Confirmar nueva contraseña"
              error={Boolean(errors.confirmPassword)}
              helperText={
                errors.confirmPassword && errors.confirmPassword.message
              }
              name="confirmPassword"
              type={confirmPasswordShown ? 'text' : 'password'}
              InputProps={{
                autoComplete: 'off',
                endAdornment: (
                  <ShowPassword
                    togglePasswordShow={onToggleConfirmPasswordShown}
                    passwordShown={confirmPasswordShown}
                  />
                )
              }}
            />
          </Grid>
          <Grid container className={classes.requestContainer}>
            <Grid item className={classes.fullWidth} sm={5}>
              <BaseButton
                color="primary"
                variant="contained"
                form="editPasswordForm"
                onClick={handleSubmit(onSubmitPassword)}
                disabled={!formState.isValid}
                fullWidth
              >
                Guardar
              </BaseButton>
            </Grid>
          </Grid>
        </form>
      )}
      <>
        {shouldShowPasswordForm() && <SimpleDivider />}
        <Grid
          item
          xs={12}
          sm={5}
          className={shouldShowPasswordForm() ? '' : classes.deleteButtonNoForm}
        >
          <BaseButton
            color="error"
            variant="outlined"
            onClick={onDeleteAccountPress}
            disabled={isDeleteAccountDisabled}
            fullWidth
          >
            Eliminar cuenta
          </BaseButton>
        </Grid>
      </>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  requestContainer: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start'
    },
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  optionWithErrors: {
    marginBottom: -17
  },
  inputFieldContainer: {
    width: '100%',
    marginBottom: theme.spacing(3)
  },
  linkButtonContainer: {
    width: '100%',
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(0)
  },
  fullWidth: {
    width: '100%'
  },
  deleteButtonNoForm: {
    marginTop: theme.spacing(2)
  },
  sweetAlert: {
    marginBottom: theme.spacing(3),
    width: '100%'
  },
  sweetAlertText: {
    fontSize: '0.9rem',
    textAlign: 'center'
  }
}));

export default MyProfileUserPassword;
